import type { WeightEntry } from '../state/weightStore'
import { parseTimestampToNumber, toIsoStringWithTimezone } from './time'

/**
 * Deserializes a stored weight entry, validating the weight and timestamp.
 *
 * @param entry - The raw stored entry.
 * @returns The weight entry, or null when invalid.
 */
export function deserializeWeightEntry(entry: unknown): WeightEntry | null {
   if (!entry || typeof entry !== 'object') {
      return null
   }

   const record = entry as Record<string, unknown>
   const weight = Number(record.weight)
   if (!Number.isFinite(weight)) {
      return null
   }

   const timestamp = parseTimestampToNumber(record.timestamp)
   if (timestamp === null) {
      return null
   }

   return {
      weight,
      timestamp,
   }
}

/**
 * Converts a weight entry to its persisted form with an ISO timestamp.
 *
 * @param entry - The in-memory weight entry.
 * @returns The persistable entry, or null when invalid.
 */
export function prepareWeightEntryForPersistence(entry: WeightEntry) {
   const weight = Number(entry.weight)
   if (!Number.isFinite(weight)) {
      return null
   }

   const isoTimestamp = toIsoStringWithTimezone(entry.timestamp)
   if (!isoTimestamp) {
      return null
   }

   return {
      weight,
      timestamp: isoTimestamp,
   }
}
